'use client'

import type { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical'
import * as React from 'react'
import { BookOpen, Flag, Lock } from 'lucide-react'
import { motion } from 'motion/react'

import { Button } from '@ui/web/components'
import { IdeaIcon, PresentationIcon, QuizIcon } from '@ui/web/icon'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import { RewardStar } from './reward-star'
import { TeacherGuide } from './teacher-guide'

export type LessonOverviewButtonState = 'locked' | 'current' | 'completed' | 'waiting' | 'failed' | 'preview'
export type LessonType = 'read' | 'quiz' | 'discovery' | 'in-class'

export const LESSON_TYPE_ICONS: Record<LessonType, React.ElementType> = {
  read: BookOpen,
  quiz: QuizIcon,
  discovery: IdeaIcon,
  'in-class': PresentationIcon,
}

export const LESSON_TYPE_INFO: Record<LessonType, { label: string; description: string }> = {
  read: {
    label: 'Membaca',
    description:
      'Aktivitas dikerjakan murid secara mandiri. Murid memperoleh poin atas upaya penyelesaian, bukan nilai asesmen. Murid dapat "Lanjut" hanya setelah sukses selesai membaca.',
  },
  'in-class': {
    label: 'Proyek',
    description:
      'Dipandu dan dinilai oleh guru di ruang kelas. Poin yang diperoleh murid setara dengan nilai asesmen yang diberikan guru. Tombol "Lanjut" hanya aktif setelah guru menandainya selesai.',
  },
  discovery: {
    label: 'Eksplorasi',
    description:
      'Dikerjakan murid secara mandiri. Murid memperoleh poin atas upaya penyelesaian, bukan nilai asesmen. Tombol "Lanjut" hanya aktif setelah murid berhasil selesai menyimak.',
  },
  quiz: {
    label: 'Kuis Latihan',
    description:
      'Dikerjakan murid secara mandiri dengan penilaian otomatis. Poin yang diperoleh setara dengan nilai yang dicapai. Tombol "Lanjut" hanya aktif setelah semua soal terjawab.',
  },
}

const FALLBACK_LESSON_TYPE_INFO = { label: 'Pelajaran', description: '' } as const

/**
 * Safely resolve lesson-type metadata. Unknown/null/undefined types (e.g. an
 * unsaved draft in live preview) fall back to a generic label instead of
 * crashing on `LESSON_TYPE_INFO[undefined].label`.
 */
export function getLessonTypeInfo(type: LessonType | null | undefined) {
  return (type && LESSON_TYPE_INFO[type]) || FALLBACK_LESSON_TYPE_INFO
}

export function getLessonTypeIcon(type: LessonType | null | undefined): React.ElementType {
  return (type && LESSON_TYPE_ICONS[type]) || BookOpen
}

interface BaseProps {
  index: number
  title: string
  lessonType: LessonType
  onClick?: () => void
  className?: string
}

export type LessonOverviewButtonProps =
  | (BaseProps & { state: 'completed'; stars: 0 | 1 | 2 | 3; guide?: never })
  | (BaseProps & { state: 'preview'; guide: unknown; stars?: never })
  | (BaseProps & {
      state: Exclude<LessonOverviewButtonState, 'completed' | 'preview'>
      stars?: never
      guide?: never
    })

export function LessonOverviewButton({
  index,
  title,
  state,
  lessonType,
  onClick,
  className,
  ...rest
}: LessonOverviewButtonProps) {
  const stars = 'stars' in rest ? rest.stars : undefined
  const guide = 'guide' in rest ? rest.guide : undefined
  const isLocked = state === 'locked' || state === 'failed'
  const isClickable = state !== 'locked' && state !== 'failed'
  const [modalOpen, setModalOpen] = React.useState(false)

  const TypeIcon = getLessonTypeIcon(lessonType)

  return (
    <div className={cn('srd:flex srd:flex-col srd:items-center srd:gap-2 srd:w-fit', className)}>
      <div className='srd:relative srd:size-27'>
        <motion.button
          type='button'
          onClick={isClickable ? onClick : undefined}
          disabled={!isClickable}
          className='srd:size-full srd:cursor-pointer srd:disabled:cursor-not-allowed srd:border-x-2 srd:border-t-2 srd:border-b-6 srd:border-neutral-white srd:rounded-4xl srd:overflow-clip'
          whileHover={isClickable ? { scale: 1.025 } : undefined}
          whileTap={isClickable ? { scale: 0.975 } : undefined}
        >
          <div
            className='srd:relative srd:w-full srd:h-full srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-0 srd:text-neutral-white'
            style={{ background: 'var(--gradient-purple-blue-bottom)' }}
          >
            <TypeIcon className={cn('srd:size-6', state === 'locked' && 'srd:opacity-50')} />
            <span
              className={cn(
                typographyVariants({ variant: 'display' }),
                'srd:text-neutral-white',
                state === 'locked' && 'srd:opacity-50',
              )}
            >
              {index}
            </span>
            {state === 'locked' && <div className='srd:absolute srd:inset-0 srd:bg-neutral-white/40' />}
          </div>
        </motion.button>

        {/* Lock badge — outside button to avoid overflow-clip */}
        {isLocked && (
          <div className='srd:absolute srd:-bottom-1 srd:-right-1 srd:size-7 srd:rounded-full srd:bg-error srd:flex srd:items-center srd:justify-center'>
            <Lock className='srd:size-5 srd:text-neutral-white' />
          </div>
        )}
      </div>

      {/* Title */}
      <p
        className={cn(
          typographyVariants({ variant: 'body-sm' }),
          'srd:text-center srd:line-clamp-2 srd:text-neutral-white srd:w-full srd:max-w-32',
        )}
      >
        {title}
      </p>

      {/* State indicators — mt-auto pins each to bottom of the card */}
      {state === 'completed' && stars !== undefined && (
        <RewardStar stars={stars} className='srd:w-13 srd:mt-auto' middleClassName='srd:-translate-y-1' />
      )}

      {state === 'waiting' && (
        <span
          className={cn(
            typographyVariants({ variant: 'label-sm' }),
            'srd:px-1 srd:py-0.5 srd:rounded-md srd:bg-blue-20 srd:text-blue-80 srd:whitespace-nowrap srd:mt-auto',
          )}
        >
          Menunggu nilai
        </span>
      )}

      {state === 'failed' && (
        <span
          className={cn(
            typographyVariants({ variant: 'label-sm' }),
            'srd:px-1 srd:py-0.5 srd:rounded-md srd:bg-error-container srd:text-error srd:whitespace-nowrap srd:mt-auto',
          )}
        >
          Gagal
        </span>
      )}

      {state === 'preview' && (
        <>
          <motion.button
            type='button'
            className='srd:flex srd:items-center srd:gap-1 srd:text-neutral-white srd:cursor-pointer srd:mt-auto'
            whileHover={{ scale: 1.025 }}
            whileTap={{ scale: 0.975 }}
            onClick={() => setModalOpen(true)}
          >
            <Flag className='srd:size-4 srd:text-neutral-white' />
            <span
              className={cn(
                typographyVariants({ variant: 'label-sm' }),
                'srd:text-neutral-white srd:whitespace-nowrap',
              )}
            >
              Panduan guru
            </span>
          </motion.button>

          <TeacherGuide
            open={modalOpen}
            onOpenChange={setModalOpen}
            title={title}
            lessonType={lessonType}
            teacherGuide={guide as SerializedEditorState | null}
          >
            <Button
              variant='outline'
              onClick={() => {
                setModalOpen(false)
                onClick?.()
              }}
            >
              Pratinjau tampilan murid
            </Button>
          </TeacherGuide>
        </>
      )}
    </div>
  )
}
